import { useCallback, useState } from 'react'

import { times } from 'lodash'

import { AddOutlined } from '@mui/icons-material'
import { Divider, Fade, List } from '@mui/material'

import { SessionPermission, TenantPermission } from '@tabeeb/enums'
import { trainingMaterialTitleFormatter } from '@tabeeb/shared/utils/text'
import { hasSessionPermission, hasTenantPermission } from '@tabeeb/modules/permissions/selectors'

import { useSelector } from '@tabeeb/store'
import {
  Button,
  SearchTextField,
  Stack,
  Subheader1,
  useDebouncedValue,
  useDialogState,
} from '@collaborate-health/uikit'
import { ContentTrainingMaterialResponse } from '@tabeeb/contracts/microservices/swagger-article-service'

import { useContentTrainingMaterials } from '../../hooks'

import ContentTrainingMaterialDialog from '../ContentTrainingMaterialDialog'
import ContentTrainingMaterialLinkButton from '../ContentTrainingMaterialLinkButton'
import ContentTrainingMaterialListItem, {
  ContentTrainingMaterialListItemSkeleton,
} from '../ContentTrainingMaterialListItem'
import ContentTrainingMaterialMenu from '../ContentTrainingMaterialMenu'
import ContentTrainingMaterialUsersDialog from '../ContentTrainingMaterialUsersDialog'

import TrainingMaterialsAttachDialog from '../TrainingMaterialsAttachDialog'
import NoTrainingMaterialsAttachedPlaceholder from '../NoTrainingMaterialsAttachedPlaceholder'

export interface ContentTrainingMaterialsTabProps {
  contentId?: number
}

const ContentTrainingMaterialsTab: React.FC<ContentTrainingMaterialsTabProps> = ({ contentId }) => {
  const [selectedTrainingMaterial, setSelectedTrainingMaterial] = useState<ContentTrainingMaterialResponse>()
  const [previewDialogOpen, onOpenPreviewDialog, onClosePreviewDialog] = useDialogState()
  const [attachDialogOpen, onOpenAttachDialog, onCloseAttachDialog] = useDialogState()
  const [manageUsersDialogOpen, onOpenManageUsersDialog, onCloseManageUsersDialog] = useDialogState()

  const [searchText, setSearchText] = useState('')
  const debouncedSearchText = useDebouncedValue(searchText, 500)

  const { loading, loaded, contentTrainingMaterials, onDetach, onReload } = useContentTrainingMaterials({
    contentId,
    search: debouncedSearchText,
  })

  const hasManagePermission = useSelector(
    (state) =>
      hasSessionPermission(state, SessionPermission.ManageTrainingMaterials) ||
      hasTenantPermission(state, TenantPermission.ManageTrainingMaterials)
  )

  const onOpenContentTrainingMaterial = useCallback(
    (contentTrainingMaterial: ContentTrainingMaterialResponse) => {
      setSelectedTrainingMaterial(contentTrainingMaterial)
      onOpenPreviewDialog()
    },
    [onOpenPreviewDialog]
  )

  const onOpenContentTrainingMaterialUsersManagement = useCallback(
    (contentTrainingMaterial: ContentTrainingMaterialResponse) => {
      setSelectedTrainingMaterial(contentTrainingMaterial)
      onOpenManageUsersDialog()
    },
    [onOpenManageUsersDialog]
  )

  const assigned = contentTrainingMaterials.filter((item) => item.Assigned)
  const notAssigned = contentTrainingMaterials.filter((item) => !item.Assigned)

  return (
    <Stack width='100%' height='100%'>
      <Stack direction='row' justifyContent='space-between' alignItems='center' minWidth={0} p={1} pt={2} pr={2}>
        <Subheader1 pl={2} noWrap>
          {trainingMaterialTitleFormatter.format('Training materials')}
        </Subheader1>

        <Fade in={hasManagePermission}>
          <Button
            size='small'
            variant='contained'
            color='primary'
            startIcon={<AddOutlined />}
            onClick={onOpenAttachDialog}
          >
            Attach
          </Button>
        </Fade>
      </Stack>

      <Stack pl={2} pr={2} pb={1}>
        <SearchTextField
          value={searchText}
          fullWidth
          placeholder='Search...'
          size='small'
          onChange={(e) => setSearchText(e.target.value)}
        />
      </Stack>

      <Stack display='flex' flexDirection='column' pl={1} pr={1} flexGrow={1} overflow='auto'>
        <List>
          {!loaded &&
            contentTrainingMaterials.length === 0 &&
            times(8, (index) => <ContentTrainingMaterialListItemSkeleton key={index} />)}

          {assigned.map((contentTrainingMaterial) => (
            <ContentTrainingMaterialListItem
              key={contentTrainingMaterial.Id}
              disabled={loading}
              item={contentTrainingMaterial}
              onDoubleClick={() => onOpenContentTrainingMaterial(contentTrainingMaterial)}
            >
              <ContentTrainingMaterialLinkButton id={contentTrainingMaterial.Id} disabled={loading} />

              <ContentTrainingMaterialMenu
                contentTrainingMaterial={contentTrainingMaterial}
                onView={onOpenContentTrainingMaterial}
                onManageUsers={hasManagePermission ? onOpenContentTrainingMaterialUsersManagement : undefined}
                onDetach={hasManagePermission ? onDetach : undefined}
              />
            </ContentTrainingMaterialListItem>
          ))}

          {notAssigned.length > 0 && <Divider variant='middle'>Not assigned</Divider>}

          {notAssigned.map((contentTrainingMaterial) => (
            <ContentTrainingMaterialListItem
              key={contentTrainingMaterial.Id}
              item={contentTrainingMaterial}
              onDoubleClick={() => onOpenContentTrainingMaterial(contentTrainingMaterial)}
            >
              <ContentTrainingMaterialLinkButton id={contentTrainingMaterial.Id} disabled={loading} />

              <ContentTrainingMaterialMenu
                contentTrainingMaterial={contentTrainingMaterial}
                onView={onOpenContentTrainingMaterial}
                onManageUsers={hasManagePermission ? onOpenContentTrainingMaterialUsersManagement : undefined}
                onDetach={hasManagePermission ? onDetach : undefined}
              />
            </ContentTrainingMaterialListItem>
          ))}
        </List>

        {loaded && contentTrainingMaterials.length === 0 && (
          <NoTrainingMaterialsAttachedPlaceholder onAttach={hasManagePermission ? onOpenAttachDialog : undefined} />
        )}
      </Stack>

      <TrainingMaterialsAttachDialog
        open={attachDialogOpen}
        contentId={contentId}
        onClose={onCloseAttachDialog}
        onUpdated={onReload}
      />

      <ContentTrainingMaterialDialog
        open={previewDialogOpen}
        contentTrainingMaterial={selectedTrainingMaterial}
        onClose={onClosePreviewDialog}
        onUpdated={onReload}
      />

      <ContentTrainingMaterialUsersDialog
        open={manageUsersDialogOpen}
        id={selectedTrainingMaterial?.Id}
        onClose={onCloseManageUsersDialog}
        onUpdated={onReload}
      />
    </Stack>
  )
}

export default ContentTrainingMaterialsTab

